import type { GraphQLFormattedError } from 'graphql';
import { type FetchResult } from '@apollo/client';
import type {
  MoveAssetsMutation,
  MovedAssetFragment,
} from '@frameio/graphql-client/generated';
import {
  MovedAsset,
  useMoveAssetsMutation,
} from '@frameio/graphql-client/generated';
import { plural } from '@frameio/i18n';
import { useDeselectAssets } from '@frameio/selection';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { useFinderRefetch } from '@frameio/shared/contexts/FinderRefetchContext';
import { useLoadingToast } from '@frameio/shared/resources';
import { hasEsmProjectError } from '@frameio/shared/utils/hasEsmProjectError';
import { useEvictFromHydrationCache } from '../../../hydration';
import type { AssetParentFragment } from '../../../shared/gql/generated/AssetParent';
import { evictAncestorFolders, refetchFolderChildren } from '../../cache-ops';
import { INVALID_TARGET_PROJECT_REASON } from '../../constants';
import TEXT from './generated/TEXT/useSyncMoveAssets';

TEXT.DEFINE(
  {
    /** Error shown when a move crosses between regular and managed project storage, in either direction. */
    invalidTargetProject:
      "Moving assets between regular and managed projects isn't supported yet.",
    loading: (count: number): string =>
      plural(count, {
        one: `Moving ${count} item…`,
        other: `Moving ${count} items…`,
      }),
    errorMessage: (count: number): string =>
      plural(count, {
        one: `Couldn't move ${count} item`,
        other: `Couldn't move ${count} items`,
      }),
    successMessage: (count: number): string =>
      plural(count, {
        one: `Moved ${count} item`,
        other: `Moved ${count} items`,
      }),
  },
  { plural }
);

type MoveAssetsProps = {
  assetIds: string[];
  sourceId: string;
  destination: AssetParentFragment;
  movePrivateCommentsToNewWorkspace?: boolean;
  onSuccess?: (destination: AssetParentFragment) => void;
  onError?: (error?: readonly GraphQLFormattedError[]) => void;
};

/**
 * useSyncMoveAssets is a hook that can be used to move a small set of assets. Sync move describes the
 * way the BE processes these requests, which is handled synchronously within the lifecycle of the request.
 */
export const useSyncMoveAssets = () => {
  const accountId = useActiveAccountId();
  const [moveAssets, { client }] = useMoveAssetsMutation();
  const refetchFinder = useFinderRefetch();
  const { addToast: addLoadingToast, updateAndHideToast } = useLoadingToast();
  const deselectIds = useDeselectAssets();
  const evictFromHydrationCache = useEvictFromHydrationCache();

  return async ({
    assetIds,
    destination,
    sourceId,
    onError,
    onSuccess,
    movePrivateCommentsToNewWorkspace,
  }: MoveAssetsProps) => {
    const toastId = addLoadingToast(TEXT.loading(assetIds.length));

    const targetProjectId = destination.project?.id ?? '';

    const handleError = (errors?: readonly GraphQLFormattedError[]) => {
      if (!errors || errors.length < 1) return;
      const esmProjectErrorMessage = hasEsmProjectError(
        client,
        targetProjectId,
        errors[0].extensions?.code,
        errors[0].extensions?.value
      );
      const errorMessage = errors.some(
        (error) => error.extensions?.reason === INVALID_TARGET_PROJECT_REASON
      )
        ? TEXT.invalidTargetProject
        : esmProjectErrorMessage ?? TEXT.errorMessage(assetIds.length);

      updateAndHideToast(toastId, {
        title: errorMessage,
        variant: 'error',
      });
      onError?.(errors);
    };

    const handleResolve = (result: FetchResult<MoveAssetsMutation>) => {
      if (result.errors || !result.data?.moveAssets?.assets?.length) {
        handleError(result.errors);
        return;
      }

      const movedAssets = result.data.moveAssets.assets ?? [];

      if (targetProjectId) {
        updateAndHideToast(
          toastId,
          {
            title: TEXT.successMessage(movedAssets.length),
            variant: 'success',
          },
          {
            folderAssetId: destination.id,
            projectId: targetProjectId,
            page: 'folder',
          }
        );
      } else {
        updateAndHideToast(toastId, {
          title: TEXT.successMessage(movedAssets.length),
          variant: 'success',
        });
      }

      deselectIds();
      evictFromHydrationCache?.([sourceId, destination.id]);
      refetchFinder();

      if (movedAssets.some((asset) => asset?.__typename === 'FolderAsset')) {
        const sources = result.data.moveAssets.sources ?? [];
        const destinationId = result.data.moveAssets.destination?.id;

        sources.forEach((source) => {
          if (source?.id) {
            refetchFolderChildren(client, accountId, source?.id);
          }
        });

        if (destinationId) {
          refetchFolderChildren(client, accountId, destinationId);
        }
      }
      onSuccess?.(destination);
    };

    const cachedAssets = assetIds.map((assetId) =>
      client.cache.readFragment<MovedAssetFragment>({
        id: client.cache.identify({
          __typename: 'Asset',
          id: assetId,
        }),
        fragment: MovedAsset,
        fragmentName: 'MovedAsset',
        returnPartialData: true,
      })
    );

    await moveAssets({
      variables: {
        input: {
          assetIds,
          parentId: destination.id,
          movePrivateCommentsToNewWorkspace,
        },
      },
      optimisticResponse: {
        __typename: 'Mutation',
        moveAssets: {
          __typename: 'MoveAssetsPayload',
          assets: cachedAssets.map((data) => {
            if (!data) return null;

            return {
              __typename: data.__typename,
              id: data.id,
              name: data.name,
              parent: {
                __typename: 'FolderAsset',
                id: destination.id,
              },
              project: {
                __typename: 'Project',
                id: destination.project?.id ?? '',
              },
            };
          }),
        },
      },
      errorPolicy: 'all',
      update(proxy, result) {
        const assets = result.data?.moveAssets?.assets?.filter(
          (asset) => asset !== null
        );

        if (!assets || assets.length === 0 || !result.data) return;

        const first = assets[0];

        if (!first.parent || !first.project) return;

        // only doing this manually because the ancestorFolders field is very
        // heavy, and we don't want to include it in the move assets payload.
        // this defers the recalculation of that field to whenever a user visits
        // one of the assets that was moved
        evictAncestorFolders(
          proxy,
          assets.map(({ id }) => id)
        );
      },
    }).then(handleResolve, handleError);
  };
};
